import { FIELD_CAN_ASK } from './variants.js';
import { choiceToken } from './choices.js';
import { INTAKE_TYPE_OF_QUESTION } from './values.js';
import { audienceIncludes } from './types.js';
/** The weight of each field (spec 3.19). */
export const SEARCH_WEIGHTS = Object.freeze({ label: 5, way: 4, phrase: 3, option: 2, section: 1, legacy: 1 });
const STOPWORDS = new Set([
    'a', 'an', 'the', 'your', 'you', 'do', 'does', 'did', 'is', 'are', 'what', 'which', 'of', 'for', 'to', 'in', 'on', 'have', 'has',
    'any', 'please', 'how', 'my', 'me', 'i', 'we', 'us', 'can', 'if', 'or', 'and',
]);
/** Light stemming for tokens longer than 4 characters: ies -> y, then strip ing, ed, es, s (one rule). */
function stem(t) {
    if (t.length <= 4)
        return t;
    if (t.endsWith('ies'))
        return `${t.slice(0, -3)}y`;
    for (const suffix of ['ing', 'ed', 'es', 's'])
        if (t.endsWith(suffix))
            return t.slice(0, -suffix.length);
    return t;
}
/** Lowercase, diacritics stripped (NFKD), anything but letters and digits a space, stopwords dropped, light stemming. */
export function tokenize(text) {
    return (text ?? '')
        .normalize('NFKD')
        .replace(/\p{M}+/gu, '')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .split(' ')
        .filter((t) => t && !STOPWORDS.has(t))
        .map(stem);
}
function field(field, parts) {
    const lists = parts.map(tokenize);
    return { field, tokens: new Set(lists.flat()), text: lists.map((l) => l.join(' ')).join(' | ') };
}
/** Every wording a question is asked by: its label, its ways to ask and the agency's own. */
const wordingsOf = (doc) => [doc.label, ...doc.ways, ...(doc.agency_ways ?? [])];
/** A document as the similarity check reads it (the index keeps one per document). */
function similarityTargetOf(doc) {
    return {
        key: doc.key,
        type: doc.type,
        wordings: wordingsOf(doc).map((w) => new Set(tokenize(w))),
        phraseSets: doc.phrases.map((w) => new Set(tokenize(w))),
        choices: new Set(doc.options.map(choiceToken)),
    };
}
/** The index of a document set (cached by the server per registry version). */
export function buildSearchIndex(docs) {
    const indexed = docs.map((doc) => {
        const fields = [
            field('label', [doc.label]),
            field('way', [...doc.ways, ...(doc.agency_ways ?? [])]),
            field('phrase', doc.phrases),
            field('option', [...doc.options, ...(doc.columns ?? [])]),
            field('section', [doc.section, doc.subsection]),
            field('legacy', doc.legacy_ids ?? []),
        ].filter((f) => f.tokens.size > 0);
        const all = new Set(fields.flatMap((f) => [...f.tokens]));
        return {
            doc,
            fields,
            optionTokens: doc.options.map((option) => ({ option, tokens: new Set(tokenize(option)) })),
            labelTokens: new Set(tokenize(doc.label)),
            all,
            similarity: similarityTargetOf(doc),
        };
    });
    const postings = new Map();
    indexed.forEach((d, i) => {
        for (const t of d.all) {
            const list = postings.get(t);
            if (list)
                list.push(i);
            else
                postings.set(t, [i]);
        }
    });
    return { docs: indexed, postings, vocabulary: [...postings.keys()].sort() };
}
/**
 * The documents with the stored ways to ask the searching agency may use added (`ways`: key -> wordings, from its own
 * and the shared question_variants), each searched as a way to ask (weight 4). Documents of other keys are returned
 * as they are; the documents passed in are never changed (the server's shared set stays cached as it is).
 */
export function withAgencyWays(docs, ways) {
    return docs.map((d) => {
        const own = ways.get(d.key);
        return own?.length ? { ...d, agency_ways: [...own] } : d;
    });
}
/** Tokens of the vocabulary that `t` is a prefix of (t itself excluded). */
function extensions(index, t) {
    const v = index.vocabulary;
    let lo = 0;
    let hi = v.length;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (v[mid] < t)
            lo = mid + 1;
        else
            hi = mid;
    }
    const out = [];
    for (let i = lo; i < v.length && v[i].startsWith(t); i++)
        if (v[i] !== t)
            out.push(v[i]);
    return out;
}
const idfOf = (index, df) => Math.log(1 + index.docs.length / (1 + df));
/** The kind a field reports as `matched` (legacy ids read as section: both are the question's metadata). */
const MATCHED_KIND = { label: 'label', way: 'way', phrase: 'phrase', option: 'option', section: 'section', legacy: 'section' };
/**
 * The option a hit narrows to (the picker's "Ask just this"): options contribute at least 60% of the score (spec
 * 3.19), or the query singles one option out: exactly one option names the most query tokens, and it names one the
 * question's label does not (the query asks for something narrower than the question: "can you drive stick").
 */
function narrowOf(d, q, byField, total) {
    if (!d.optionTokens.length || total <= 0)
        return undefined;
    const counts = d.optionTokens.map((o) => ({ option: o.option, n: q.filter((t) => o.tokens.has(t)).length, own: q.some((t) => o.tokens.has(t) && !d.labelTokens.has(t)) }));
    const best = Math.max(...counts.map((c) => c.n));
    if (best === 0)
        return undefined;
    const top = counts.filter((c) => c.n === best);
    if (top.length !== 1)
        return undefined;
    if ((byField.get('option') ?? 0) / total >= 0.6 || top[0].own)
        return { option: top[0].option };
    return undefined;
}
/** Search the index (spec 3.19): the best hits first, at most `limit`. */
export function searchIndex(index, query, opts = {}) {
    const q = [...new Set(tokenize(query))];
    if (!q.length)
        return [];
    const phrase = q.join(' ');
    const expansions = new Map(q.map((t) => [t, t.length >= 3 ? extensions(index, t) : []]));
    const candidates = new Set();
    for (const t of q) {
        for (const i of index.postings.get(t) ?? [])
            candidates.add(i);
        for (const e of expansions.get(t))
            for (const i of index.postings.get(e) ?? [])
                candidates.add(i);
    }
    const idf = new Map(q.map((t) => [t, idfOf(index, index.postings.get(t)?.length ?? 0)]));
    const scored = [];
    for (const i of candidates) {
        const d = index.docs[i];
        const doc = d.doc;
        if (opts.section && doc.section_key !== opts.section)
            continue;
        if (opts.audience && !audienceIncludes(doc.audience, opts.audience))
            continue;
        const byField = new Map();
        const matchedTokens = new Set();
        for (const f of d.fields) {
            const w = SEARCH_WEIGHTS[f.field];
            let s = 0;
            for (const t of q) {
                if (f.tokens.has(t)) {
                    s += w * idf.get(t);
                    matchedTokens.add(t);
                }
                else if (expansions.get(t).some((e) => f.tokens.has(e))) {
                    s += 0.5 * w * idf.get(t);
                    matchedTokens.add(t);
                }
            }
            if (` ${f.text} `.includes(` ${phrase} `))
                s += 2 * w;
            if (s > 0)
                byField.set(f.field, s);
        }
        if (!matchedTokens.size)
            continue;
        const raw = [...byField.values()].reduce((a, b) => a + b, 0);
        const used = doc.used_by_agency || opts.used?.has(doc.key);
        const score = raw * Math.sqrt(matchedTokens.size / q.length) * (used ? 1.1 : 1);
        let top = 'label';
        for (const [f, s] of byField)
            if (s > (byField.get(top) ?? 0))
                top = f;
        const narrow = narrowOf(d, q, byField, raw);
        scored.push({ doc, hit: { key: doc.key, score: Math.round(score * 1000) / 1000, matched: MATCHED_KIND[top], ...(narrow ? { narrow } : {}) } });
    }
    scored.sort((a, b) => b.hit.score - a.hit.score || (a.doc.shelf === b.doc.shelf ? 0 : a.doc.shelf === 'shared' ? -1 : 1) || a.doc.label.localeCompare(b.doc.label));
    return scored.slice(0, opts.limit ?? 20).map((s) => s.hit);
}
/** The bands of spec 3.19: strong from 0.75, possible from 0.5, else not similar (null). */
export function similarBand(score) {
    return score >= 0.75 ? 'strong' : score >= 0.5 ? 'possible' : null;
}
/** |a and b| / |a or b| over two token sets (0 when both are empty). */
function jaccard(a, b) {
    if (!a.size && !b.size)
        return 0;
    let both = 0;
    for (const t of a)
        if (b.has(t))
            both++;
    return both / (a.size + b.size - both);
}
/**
 * The draft's type can ask the question: the same type, or one whose intake field asks it (variants FIELD_CAN_ASK:
 * a yes/no or a list of one choice asks a choice question, a narrowed way; free text asks anything but a file or a grid).
 */
export function draftCanAsk(draftType, questionType) {
    if (!draftType || !questionType || draftType === questionType)
        return true;
    return FIELD_CAN_ASK[INTAKE_TYPE_OF_QUESTION[draftType]].includes(questionType);
}
/** The best jaccard of the draft's tokens with any one of these token sets. */
const bestJaccard = (tokens, sets) => sets.reduce((best, set) => Math.max(best, jaccard(tokens, set)), 0);
const draftTokens = (draft) => ({
    type: draft.type,
    tokens: new Set(tokenize(draft.label)),
    options: (draft.options ?? []).map(choiceToken).filter(Boolean),
});
/**
 * THE similarity (spec 3.19): 0.6 * jaccard(draft tokens, the label or one way to ask: the best single wording, since
 * the union of every wording would dilute any match) + 0.25 * phrase score (the best jaccard with one search phrase)
 * + 0.15 * option overlap (the share of the draft's options the question holds, compared as choice tokens; 0 when
 * none given). 0 when the draft's type cannot ask the question's (draftCanAsk) or the draft has no words.
 */
function similarityOf(draft, target) {
    if (!draft.tokens.size || !draftCanAsk(draft.type, target.type))
        return 0;
    const overlap = draft.options.length ? draft.options.filter((o) => target.choices.has(o)).length / draft.options.length : 0;
    return 0.6 * bestJaccard(draft.tokens, target.wordings) + 0.25 * bestJaccard(draft.tokens, target.phraseSets) + 0.15 * overlap;
}
/** The similarity of a draft to one question, 0 to 1 (0 when the draft's type cannot ask it); similarBand bands it. */
export function similarityScore(draft, question) {
    return similarityOf(draftTokens(draft), similarityTargetOf(searchDocOf(question, 'shared', '')));
}
/** The questions a draft is strong or possible for, best first (ties by key), at most `limit`. */
function rankSimilar(draft, targets, opts) {
    const d = draftTokens(draft);
    if (!d.tokens.size)
        return [];
    const out = [];
    for (const target of targets) {
        if (opts.exclude?.has(target.key))
            continue;
        const score = Math.round(similarityOf(d, target) * 1000) / 1000; // the band follows the score as reported and stored
        const band = similarBand(score);
        if (band)
            out.push({ key: target.key, score, band });
    }
    return out.sort((a, b) => b.score - a.score || a.key.localeCompare(b.key)).slice(0, opts.limit ?? 5);
}
/** Questions of a search index like a custom one being typed (the picker's "Did you mean" and SimilarityPrompt). */
export function similarTo(index, draft, opts = {}) {
    return rankSimilar(draft, index.docs.map((d) => d.similarity), opts);
}
/**
 * Questions like a custom one among any set passed in (the library inbox's candidates: the shared shelf, the
 * overlay and every agency's custom questions; spec 3.21), best first.
 */
export function similarQuestions(draft, questions, opts = {}) {
    return rankSimilar(draft, questions.map((q) => similarityTargetOf(searchDocOf(q, 'shared', ''))), opts);
}
/** A library (or agency) question as a search document (`sectionTitle`: its section's title). */
export function searchDocOf(q, shelf, sectionTitle) {
    return {
        key: q.key,
        label: q.label,
        ways: q.ways_to_ask.map((w) => w.wording),
        phrases: q.search_phrases,
        options: [...(q.options ?? [])],
        columns: [...(q.columns ?? [])],
        section: sectionTitle,
        subsection: q.subsection,
        legacy_ids: q.legacy_keys,
        shelf,
        section_key: q.section,
        audience: q.audience,
        type: q.type,
    };
}
//# sourceMappingURL=search.js.map